import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { usePoses } from '../data/poses';
import { HowItWorksModal } from './HowItWorks';
import { AuthPanel } from './AuthPanel';
import { SettingsPanel } from './SettingsPanel';

/**
 * Single header dropdown consolidating Settings and account actions (Sign in /
 * Sign out). Settings is always available; account items appear only when a
 * sync backend is configured.
 */
export function HeaderMenu() {
  const { enabled, status, user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<null | 'settings' | 'auth' | 'how'>(null);
  const poses = usePoses();
  const ref = useRef<HTMLDivElement>(null);

  // Close the dropdown on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const authed = enabled && status === 'authenticated' && user;
  // Greet people by name once we have one; older accounts predate `given_name`,
  // so fall back to the email's local part rather than showing the full address.
  const label = user ? user.firstName || user.email.split('@')[0] : 'Menu';

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className="btn ghost menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        title={user ? user.email : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {/* Phones get the conventional hamburger; wider screens get the name. */}
        <svg
          className="menu-burger"
          width="18"
          height="14"
          viewBox="0 0 18 14"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M1 1h16M1 7h16M1 13h16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <span className="menu-user">{label}</span>
        <span className="menu-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="menu-dropdown" role="menu">
          {authed && (
            <div className="menu-account">
              {user.firstName && (
                <div className="menu-name">
                  {user.firstName}
                  {user.isAdmin && <span className="menu-admin">★ Admin</span>}
                </div>
              )}
              <div className="menu-email" title={user.email}>
                {user.email}
                {!user.firstName && user.isAdmin && <span className="menu-admin">★ Admin</span>}
              </div>
            </div>
          )}
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => {
              setModal('how');
              setOpen(false);
            }}
          >
            How it works
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => {
              setModal('settings');
              setOpen(false);
            }}
          >
            Settings
          </button>
          {authed && user.isAdmin && (
            <button
              type="button"
              role="menuitem"
              className="menu-item"
              onClick={() => {
                window.location.hash = '#/admin/feedback';
                setOpen(false);
              }}
            >
              Feedback requests
            </button>
          )}
          {enabled &&
            (authed ? (
              <button
                type="button"
                role="menuitem"
                className="menu-item"
                onClick={() => {
                  signOut();
                  setOpen(false);
                }}
              >
                Sign out
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                className="menu-item"
                disabled={status === 'loading'}
                onClick={() => {
                  setModal('auth');
                  setOpen(false);
                }}
              >
                {status === 'loading' ? 'Loading…' : 'Sign in or sign up'}
              </button>
            ))}
        </div>
      )}

      {modal === 'how' && (
        <HowItWorksModal poseCount={poses.length} onClose={() => setModal(null)} />
      )}
      {modal === 'settings' && <SettingsPanel onClose={() => setModal(null)} />}
      {modal === 'auth' && <AuthPanel onClose={() => setModal(null)} />}
    </div>
  );
}
